import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Container from 'app/common/narrow-container';
import Paper from '@mui/material/Paper';
import Button from 'app/common/button';
import Input from 'app/common/form/input';
import { useMutation } from '../hooks/useMutation';
import { login } from 'app/redux/modules/auth';
import { connect } from 'react-redux';
import { toastr } from 'app/common/toastr';
import { decode } from 'app/lib/decodeToken.js';
import i18n from 'app/common/i18n';

const RESET_PASSWORD = `
mutation ResetPassword($password: String! $userId: ID! $email: String!) {
    userResetPassword(password: $password, userId: $userId, email: $email) {
        token
    }
}
`;

function validation(value, setError) {
  if (!value.password || value.password.length < 6) {
    setError('Din adgangskode skal mindst være 6 tegn');
    return;
  }

  if (value.password !== value.repeatPassword) {
    setError('Din adgangskode er ikke ens');
    return;
  }

  return true;
}

function getTokenFromSearch(search) {
  const query = new URLSearchParams(search || '');
  const rawToken = query.get('token');

  if (!rawToken) {
    return null;
  }

  try {
    return decode(rawToken);
  } catch (err) {
    return null;
  }
}

const ResetPassword = (props) => {
  const location = useLocation();
  const navigate = useNavigate();

  const token = getTokenFromSearch(location.search);

  const tokenValid =
    token &&
    token.id &&
    token.email &&
    token.exp &&
    Number(token.exp) * 1000 > Date.now();

  const { mutate } = useMutation(RESET_PASSWORD);
  const [value, setValue] = React.useState({});
  const [error, setError] = React.useState(null);

  const onSubmitHandler = async (ev) => {
    ev.preventDefault();

    if (!tokenValid) {
      setError('Linket er udløbet eller ugyldigt');
      return;
    }

    if (validation(value, setError) === true) {
      const { data } = await mutate({
        password: value.password,
        userId: token.id,
        email: token.email
      });

      if (data && data.userResetPassword && data.userResetPassword.token) {
        setError(null);

        props
          .login({ username: token.email, password: value.password })
          .then((action) => {
            if (action.error) {
              const { error: { response } = {} } = action || {};

              setError(
                (response && props.i18n(response.message)) ||
                  props.i18n(
                    'Oops! Something went wrong. Please try again or contact customer support'
                  )
              );
            }

            return action;
          })
          .then(() => {
            toastr.success(props.i18n('Din adgangskode er blevet ændret'));
            navigate('/dwellings', { replace: true });
          });
      }
    }
  };

  const onChangeHandler = (ev) => {
    const { name, value } = ev.currentTarget;
    setValue((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Container header="Nulstil adgangskoden">
      <Paper
        component="form"
        className="login"
        onSubmit={onSubmitHandler}
        style={{ marginBottom: '1em', padding: '1em' }}
      >
        {!tokenValid ? (
          <>
            <p style={{ marginTop: 0 }}>
              Dette link er udløbet, få et nyt link ved at nulstil din
              adgangskode
            </p>
            <Button primary to="/forgot-password" label="Nulstil adgangskode" />
          </>
        ) : (
          <>
            <Input
              fullWidth
              name="password"
              onChange={onChangeHandler}
              required
              type="password"
              label="Password"
            />
            <Input
              fullWidth
              name="repeatPassword"
              onChange={onChangeHandler}
              required
              type="password"
              label="Gentag password"
            />
            <p style={{ color: error ? 'red' : undefined }}>{error}</p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '1em'
              }}
            >
              <Button primary type="submit" label="Nulstil adgangskode" />
            </div>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default i18n(
  connect(({ auth: { user } }) => ({ user }), { login })(ResetPassword)
);
